import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import * as Sentry from '@sentry/react';
import { getPlayerToken } from '../playerToken';

interface MinimalSocket {
  on(event: string, callback: (...args: any[]) => void): void;
  emit(event: string, ...args: any[]): void;
  disconnect(): void;
}

function JoinRoom() {
  const [name, setName] = useState<string>('');
  const [roomCode, setRoomCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [roomCodeError, setRoomCodeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const socketRef = useRef<MinimalSocket | null>(null);
  const roomCodeRef = useRef<string>('');
  const nameRef = useRef<string>('');
  const navigate = useNavigate();
  const location = useLocation();

    useEffect(() => {
      nameRef.current = name.trim();
      roomCodeRef.current = roomCode.trim().toUpperCase();
    }, [name, roomCode]);
  
  useEffect(() => {
    const serverUrl = import.meta.env.VITE_SERVER_URL;
    const newSocket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = newSocket;

    newSocket.on('connect_error', (err: Error) => {
      Sentry.captureException(err);
      setError('Failed to connect to server. Please try again.');
    });

    newSocket.on('joinValidationResult', (result: { success: boolean; message?: string }) => {
      if (result.success) {
        const trimmedName = nameRef.current;
        const trimmedRoomCode = roomCodeRef.current;
        if (!trimmedRoomCode || trimmedRoomCode.length !== 4) {
          setError('Invalid room code during navigation');
          return;
        }
        sessionStorage.setItem('playerName', trimmedName);
        navigate(`/game/${trimmedRoomCode}`, { state: { playerName: trimmedName } });
      } else {
        setError(result.message || 'An error occurred. Please try again.');
      }
    });

    newSocket.on('error', (msg: string) => {
      setError(msg);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [navigate]);

    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const roomCodeParam = params.get('roomCode');
      if (roomCodeParam) {
        const validRoomCode = roomCodeParam.toUpperCase().match(/^[A-Z]{0,4}$/) ? roomCodeParam.toUpperCase() : '';
        setRoomCode(validRoomCode);
      }
    }, [location]);  

    const handleRoomCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toUpperCase();
      // Allow only A-Z, up to 4 characters
      if (/^[A-Z]{0,4}$/.test(value)) {
        setRoomCode(value);
        setRoomCodeError(value.length !== 4 ? 'Room code must be exactly 4 letters (A-Z)' : null);
      }
    };
  
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toUpperCase();
      // Allow letters, numbers, space, hyphen, underscore, up to 20 characters
      if (/^[A-Z0-9 _-]{0,20}$/.test(value)) {
        setName(value);
        setNameError(value.trim().length === 0 ? 'Name cannot be empty' : null);
      }
    };
  
  const joinRoom = () => {
    const trimmedName = name.trim().toUpperCase();
    const trimmedRoomCode = roomCode.trim().toUpperCase();

    if (!trimmedName) {
      setNameError('Name cannot be empty');
      return;
    }
    if (!/^[A-Z0-9 _-]{1,20}$/.test(trimmedName)) {
      setNameError('Name can only contain letters, numbers, spaces, hyphens, or underscores (max 20)');
      return;
    }
    if (trimmedRoomCode.length !== 4 || !/^[A-Z]{4}$/.test(trimmedRoomCode)) {
      setRoomCodeError('Room code must be exactly 4 letters (A-Z)');
      return;
    }

    if (socketRef.current) {
      setError(null);
      setRoomCodeError(null);
      setNameError(null);
      const playerToken = getPlayerToken(trimmedRoomCode) || undefined;
      socketRef.current.emit('validateJoin', { roomCode: trimmedRoomCode, playerName: trimmedName, playerToken });
    } else {
      setError('Not connected to server. Please try again.');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <h1 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">Join a Room</h1>
      <input
        type="text"
        placeholder="Your Name"
        value={name}
        onChange={handleNameChange}
        maxLength={20}
        className={`mb-4 p-3 border rounded-lg w-full max-w-xs bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${
          nameError ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {nameError && <p className="text-red-400 mb-4">{nameError}</p>}
      <input
        type="text"
        placeholder="Room Code"
        value={roomCode}
        onChange={handleRoomCodeChange}
        maxLength={4}
        className={`mb-6 p-3 border rounded-lg w-full max-w-xs bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${
          roomCodeError ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {roomCodeError && <p className="text-red-400 mb-4">{roomCodeError}</p>}
      {error && <p className="text-red-400 mb-4">{error}</p>}
      <button
        onClick={joinRoom}
        className={`bg-green-600 text-white font-semibold px-8 py-4 rounded-lg shadow-md transition duration-300 text-xl ${
          !name.trim() || roomCode.length !== 4 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
        }`}
        disabled={!name.trim() || roomCode.length !== 4}
      >
        Join
      </button>
    </div>
  );
}

export default JoinRoom;
